import { TileId } from '../../../../shared/models/tile';
import { AppId } from '../../../../shared/models/app';
import { FormulaConfig } from '../../../../shared/models/formula-config';
import {
  BACNET_POINT_COLUMNS,
  MODBUS_POINT_COLUMNS,
  LORAWAN_POINT_COLUMNS,
  TILE_CREATION_CONFIG_COLUMNS,
  TOGGLE_SPECIFIC_COLUMNS,
  NUMERIC_SPECIFIC_COLUMNS,
  ColumnsToInterface
} from '../constants/csv-columns.constants';

// Base tile creation configuration interface (includes app and super tile linking)
type CSVTileConfig = ColumnsToInterface<typeof TILE_CREATION_CONFIG_COLUMNS>;

// Protocol-specific interfaces (all optional)
type CSVBACnetColumns = Partial<ColumnsToInterface<typeof BACNET_POINT_COLUMNS>>;
type CSVModbusColumns = Partial<ColumnsToInterface<typeof MODBUS_POINT_COLUMNS>>;
type CSVLoRaWANColumns = Partial<ColumnsToInterface<typeof LORAWAN_POINT_COLUMNS>>;

// Toggle tile specific columns
type CSVToggleColumns = CSVTileConfig & ColumnsToInterface<typeof TOGGLE_SPECIFIC_COLUMNS>;

// Numeric tile specific columns
type CSVNumericColumns = CSVTileConfig & ColumnsToInterface<typeof NUMERIC_SPECIFIC_COLUMNS>;

// Final row interfaces combining protocol-specific columns (all optional)
export type CSVToggleTileRow = CSVToggleColumns & CSVBACnetColumns & CSVModbusColumns & CSVLoRaWANColumns;
export type CSVNumericTileRow = CSVNumericColumns & CSVBACnetColumns & CSVModbusColumns & CSVLoRaWANColumns;

export type CSVTileRow = CSVToggleTileRow | CSVNumericTileRow;

export enum TileProcessingStatus {
  QUEUED = 'queued',
  PROCESSING = 'processing',
  LINKING_APPS = 'linking_apps',
  LINKING_TAGS = 'linking_tags',
  AWAITING_FOLDER_LINKING = 'awaiting_folder_linking',
  LINKING_SUPER_TILES = 'linking_super_tiles',
  CREATED = 'created',
  UPDATED = 'updated',
  ERROR_PERMISSIONS = 'error_permissions',
  ERROR_GENERAL = 'error_general',
  ERROR_APP_LINKING = 'error_app_linking',
  ERROR_FOLDER_LINKING = 'error_folder_linking',
  ERROR_LINKING = 'error_linking',
  ERROR_TAG_LINKING = 'error_tag_linking',
  WARNING_PARTIAL = 'warning_partial'
}

// Fractional progress credit per in-progress status (terminal states count as 1)
export const TILE_PROCESSING_PROGRESS_WEIGHT: Partial<Record<TileProcessingStatus, number>> = {
  [TileProcessingStatus.PROCESSING]:              0.2,
  [TileProcessingStatus.LINKING_APPS]:            0.4,
  [TileProcessingStatus.LINKING_TAGS]:            0.5,
  [TileProcessingStatus.AWAITING_FOLDER_LINKING]: 0.6,
  [TileProcessingStatus.LINKING_SUPER_TILES]:     0.8,
};

export enum ProcessingState {
  IDLE = 'idle',
  PROCESSING = 'processing',
  PAUSED = 'paused',
  COMPLETED = 'completed'
}

export interface TileProcessingItem {
  id: string;
  csvData: CSVTileRow;
  tileId?: TileId;
  tile?: any; // Store the actual Tile object once created
  formulaConfig?: FormulaConfig; // Formula config for formula tiles (used during device creation)
  originalTile?: any; // Store the original Tile state before updates (for revert)
  status: TileProcessingStatus;
  error?: string;
  linkedApps?: AppId[]; // Apps that were successfully linked (for creation) or newly linked (for configuration)
  unlinkedApps?: AppId[]; // Apps that were unlinked (for configuration only)
  linkedSuperTiles?: TileId[];
  failedLinks?: {
    apps?: AppId[]; // Failed app links (for creation mode)
    appsToLink?: AppId[]; // Failed app links (for configuration mode)
    appsToUnlink?: AppId[]; // Failed app unlinks (for configuration mode)
    superTiles?: TileId[];
  };
  tagLinkingFailed?: boolean;
}

export interface BatchProcessingConfig {
  batchSize: number;
}

export const DEFAULT_BATCH_CONFIG: BatchProcessingConfig = {
  batchSize: 5  // Maximum 5 tiles processing at once
};

export interface ProcessingStatistics {
  total: number;
  queued: number;
  processing: number;
  created: number;
  errors: number;
  warnings: number;
  progressPercentage: number;
}
